import { useMutation } from '@tanstack/react-query'

import { createApiCall } from 'services'

/**
 * VRG-455 — the checkout-email recovery path, on the EXISTING account-link +
 * OTP mechanism.
 *
 * ## Why this is recovery, not account settings
 *
 * The identity is the Vera ACCOUNT (a user id). It is permanent, it survives an
 * address change, and ownership attaches to it. `buyer_email` on a recorded
 * sale is a compromise rather than a design choice — an order carries an
 * address, not a Vera user id — so the address is a lookup key used ONCE, at
 * claim time, and inert afterwards.
 *
 * That is why the copy must not read as "add another email to your account".
 * That framing implies the email IS the identity, which is the one thing this
 * model says it is not. The buyer's problem is "I bought this and you can't
 * find it"; linking the address is the mechanism, not the errand.
 *
 * ## No second verification mechanism
 *
 * `POST /account-link/link` sends a one-time code TO the entered address and
 * stores its reference; `POST /account-link/confirm` nulls that reference once
 * the code comes back. A null `otp_reference` on a link row is the proof of
 * control, and it is exactly what the resolve lookup's email anchors are drawn
 * from (`getConfirmedEmails`). Verification is non-negotiable: without it,
 * anyone could consume another person's entitlement by typing their address.
 *
 * ⚠️ KNOWN LIMIT, inherited rather than introduced. `AccountLinkController`
 * hardcodes `CompanyProvider.Shopify`, and `LinkAccountUseCase` allows ONE link
 * row per (account, provider) — a second, different address is rejected with
 * "Account already linked with different provider account". So a buyer who
 * ordered under two addresses can recover one of them, not both. Widening that
 * is a change to the account-link model, not to this flow, and is reported
 * rather than worked around here.
 */
export interface LinkCheckoutEmailResponse {
  otpReference: string
}

export class CheckoutEmailLinkError extends Error {
  readonly status: number | null

  constructor(status: number | null, message: string) {
    super(message)
    this.name = 'CheckoutEmailLinkError'
    this.status = status
  }
}

const readError = (response: unknown, fallback: string): CheckoutEmailLinkError => {
  const status = (response as { status?: number } | undefined)?.status ?? null
  const message =
    (response as { data?: { message?: string } } | undefined)?.data?.message || fallback

  return new CheckoutEmailLinkError(status, message)
}

const isOk = (response: unknown): boolean => {
  const status = (response as { status?: number } | undefined)?.status
  return typeof status === 'number' && status >= 200 && status < 300
}

/** Step 1 — send a code to the address the buyer used at checkout. */
export const useSendCheckoutEmailCode = () =>
  useMutation<LinkCheckoutEmailResponse, CheckoutEmailLinkError, { email: string }>({
    mutationFn: async ({ email }) => {
      const response = await createApiCall({
        method: 'POST',
        url: '/account-link/link',
        data: { email: email.trim().toLowerCase() },
      })

      if (!isOk(response)) {
        throw readError(response, "We couldn't send a code to that address.")
      }

      return response.data as LinkCheckoutEmailResponse
    },
  })

/** Step 2 — confirm the code, which is what makes the address a usable anchor. */
export const useConfirmCheckoutEmailCode = () =>
  useMutation<
    void,
    CheckoutEmailLinkError,
    { otpCode: string; otpReference: string }
  >({
    mutationFn: async ({ otpCode, otpReference }) => {
      const response = await createApiCall({
        method: 'POST',
        url: '/account-link/confirm',
        data: { otpCode, otpReference },
      })

      if (!isOk(response)) {
        throw readError(response, "That code didn't work. Check it and try again.")
      }
    },
  })
